import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureQuery } from '@/hooks/useSecureQuery';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, ArrowUpDown, ArrowDown, ArrowUp, Settings, Download, Upload } from 'lucide-react';
import { format } from 'date-fns';

interface InventoryTransaction {
  id: string;
  inventory_item_id: string;
  transaction_type: string;
  quantity: number;
  unit_cost?: number;
  reference_type?: string;
  reference_id?: string;
  notes?: string;
  created_at: string;
  user_id?: string;
  inventory_item_name: string;
  inventory_item_sku: string;
  inventory_item_type: string;
}

export const InventoryTransactions = () => {
  const [filterType, setFilterType] = useState<string>('all');

  // Fetch transactions using a manual approach to avoid relationship errors
  const { data: transactions = [], isLoading, error } = useSecureQuery(
    ['inventory-transactions', filterType],
    async () => {
      // First, get all transactions
      let transactionQuery = supabase
        .from('inventory_transactions')
        .select(`
          id,
          inventory_item_id,
          transaction_type,
          quantity,
          unit_cost,
          reference_type,
          reference_id,
          notes,
          created_at,
          user_id
        `)
        .order('created_at', { ascending: false })
        .limit(200);

      if (filterType !== 'all') {
        transactionQuery = transactionQuery.eq('transaction_type', filterType);
      }

      const { data: transactionsData, error: transactionsError } = await transactionQuery;
      
      if (transactionsError) {
        console.error('Error fetching transactions:', transactionsError);
        throw transactionsError;
      }

      if (!transactionsData || transactionsData.length === 0) {
        return [];
      }

      // Get unique inventory item IDs
      const itemIds = [...new Set(transactionsData.map(t => t.inventory_item_id))];
      
      // Fetch inventory items separately
      const { data: itemsData, error: itemsError } = await supabase
        .from('inventory_items')
        .select('id, name, sku, type')
        .in('id', itemIds);

      if (itemsError) {
        console.error('Error fetching inventory items:', itemsError);
        throw itemsError;
      }

      // Create a map for quick lookup
      const itemsMap = new Map(itemsData?.map(item => [item.id, item]) || []);
      
      // Combine the data
      const combinedData = transactionsData.map(transaction => {
        const item = itemsMap.get(transaction.inventory_item_id);
        return {
          ...transaction,
          inventory_item_name: item?.name || 'Unknown Item',
          inventory_item_sku: item?.sku || 'N/A',
          inventory_item_type: item?.type || 'Unknown',
        };
      });
      
      return combinedData as InventoryTransaction[];
    },
    {
      requiresAuth: true,
    }
  );

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'received':
      case 'produced':
        return <ArrowDown className="h-4 w-4 text-green-500" />;
      case 'used':
      case 'sold':
        return <ArrowUp className="h-4 w-4 text-red-500" />;
      case 'adjusted':
        return <Settings className="h-4 w-4 text-blue-500" />;
      case 'reserved':
        return <Download className="h-4 w-4 text-yellow-500" />;
      case 'cancelled':
        return <Upload className="h-4 w-4 text-gray-500" />;
      default:
        return <ArrowUpDown className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTransactionBadge = (type: string) => {
    const config: Record<string, { variant: any; color: string }> = {
      received: { variant: 'default', color: 'text-green-700 bg-green-100' },
      produced: { variant: 'default', color: 'text-green-700 bg-green-100' },
      used: { variant: 'destructive', color: 'text-red-700 bg-red-100' },
      sold: { variant: 'destructive', color: 'text-red-700 bg-red-100' },
      reserved: { variant: 'secondary', color: 'text-yellow-700 bg-yellow-100' },
      cancelled: { variant: 'outline', color: 'text-gray-700 bg-gray-100' },
      adjusted: { variant: 'outline', color: 'text-blue-700 bg-blue-100' },
    };
    
    const { variant } = config[type] || { variant: 'default' };
    return (
      <Badge variant={variant} className="flex items-center gap-1">
        {getTransactionIcon(type)}
        {type?.toUpperCase() || 'UNKNOWN'}
      </Badge>
    );
  };

  const formatQuantity = (quantity: number, type: string) => {
    const isOutgoing = ['used', 'sold'].includes(type);
    const sign = isOutgoing ? '-' : '+';
    const color = isOutgoing ? 'text-red-600' : 'text-green-600';
    
    return (
      <span className={color}>
        {sign}{quantity}
      </span>
    );
  };

  const transactionTypes = [
    { value: 'all', label: 'All Transactions' },
    { value: 'received', label: 'Received' },
    { value: 'used', label: 'Used' },
    { value: 'produced', label: 'Produced' },
    { value: 'sold', label: 'Sold' },
    { value: 'reserved', label: 'Reserved' },
    { value: 'adjusted', label: 'Adjusted' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  if (error) {
    console.error('Transactions query error:', error);
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-600">
            Error loading transactions: {(error as any)?.message || 'Unknown error'}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Transaction Log
            </CardTitle>
            <CardDescription>
              Read-only log of all inventory movements and changes
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                {transactionTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Unit Cost</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                      <span className="ml-2">Loading transactions...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {filterType === 'all' 
                      ? 'No transactions found. Transactions will appear here automatically as inventory changes occur.'
                      : `No ${filterType} transactions found.`
                    }
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell className="font-mono text-sm">
                      {format(new Date(transaction.created_at), 'MMM dd, yyyy')}
                      <br />
                      <span className="text-muted-foreground text-xs">
                        {format(new Date(transaction.created_at), 'HH:mm:ss')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{transaction.inventory_item_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {transaction.inventory_item_sku} • {transaction.inventory_item_type}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getTransactionBadge(transaction.transaction_type)}
                    </TableCell>
                    <TableCell className="font-mono">
                      {formatQuantity(transaction.quantity, transaction.transaction_type)}
                    </TableCell>
                    <TableCell>
                      {transaction.unit_cost ? (
                        <span className="font-mono">${transaction.unit_cost.toFixed(2)}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {transaction.reference_type && transaction.reference_id ? (
                        <div className="text-sm">
                          <div className="font-medium capitalize">{transaction.reference_type.replace('_', ' ')}</div>
                          <div className="text-muted-foreground font-mono text-xs">
                            {transaction.reference_id.substring(0, 8)}...
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-48">
                      {transaction.notes ? (
                        <span className="text-sm" title={transaction.notes}>
                          {transaction.notes.length > 50 
                            ? `${transaction.notes.substring(0, 50)}...`
                            : transaction.notes
                          }
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {transactions.length > 0 && (
          <div className="flex items-center justify-between pt-4 text-sm text-muted-foreground">
            <span>
              Showing {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
              {filterType !== 'all' && ` (${filterType} only)`}
            </span>
            {filterType !== 'all' && (
              <button
                className="text-primary hover:underline"
                onClick={() => setFilterType('all')}
              >
                Show all transactions
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};